<template>
  <div
    class="full-image-carousel critical-component-clear"
    :class="[
      liquid.layout === 'fullscreen' && 'full-image-carousel--fullscreen',
      liquid.mediaOptions === 'image' &&
        `full-image-carousel--image-overlay-${liquid.overlayOptions}`,
      liquid.mediaOptions === 'video' &&
        `full-image-carousel--video-overlay-${liquid.overlayOptions}`,
      `full-image-carousel--horizontal-alignment-${liquid.horizontalAlignmentOptions}`,
      `full-image-carousel--vertical-alignment-${liquid.verticalAlignmentOptions}`,
      liquid.horizontalAlignmentOptions === 'center' && 'text-center',
    ]"
  >
    <div class="full-image-carousel__container">
      <div class="full-image-carousel__media-container">
        <!-- Video Player -->
        <video-player
          v-if="liquid.mediaOptions === 'video' && (liquid.mobileVideo || liquid.desktopVideo)"
          class="full-image-carousel__video-player"
          :image="isMobile ? liquid.mobileVideo.preview_image : liquid.desktopVideo.preview_image"
          :video="videoSource"
          :intersection-observer="{ enable: true }"
          :lazyload="liquid.lazyload"
          is-active
        />

        <!-- Image Slideshow -->
        <Splide
          v-if="liquid.mediaOptions === 'image' && slides.length"
          :options="splideOptions"
          @splide:mounted="onSplideMounted"
          @splide:moved="onSplideMoved"
        >
          <SplideSlide v-for="(slide, index) in slides" :key="index">
            <responsive-image
              v-if="isMobile"
              class='full-image-banner__image full-image-banner__image--mobile'
              sizes="100vw"
              :padding="liquid.responsiveImagePadding"
              min-max="1200-2400"
              :image="slide.mobileImage"
            />

            <responsive-image
              v-else
              class='full-image-banner__image full-image-banner__image--desktop'
              :image="slide.desktopImage"
              :padding="liquid.responsiveImagePadding"
              sizes="100vw"
              min-max="1200-2400"
            />
          </SplideSlide>
        </Splide>
      </div>

      <div class="full-image-carousel__content-container">
        <div
          v-if="liquid.mediaOptions === 'image' && slides.length"
          class="full-image-carousel__slide-content"
        >
          <transition-group name="fade-up" tag="div">
            <div
              v-for="(slide, index) in slides"
              :key="index"
              v-show="index === currentSlideIndex"
            >
              <component
                :is="index == 0 ? text.headingTag : `h2`"
                v-if="slide.titleHtml"
                :class="`
                  full-image-carousel__heading
                  full-image-carousel__slide-title
                  ${text.color}
                  ${text.desktopHeader}
                  ${text.mobileHeader}
                `"
                v-html="slide.titleHtml"
              />

              <div
                v-if="slide.subHeading"
                :class="`
                  full-image-carousel__sub-heading
                  ${text.color}
                  ${text.desktopSubHeader}
                  ${text.mobileSubHeader}
                `"
                v-text="slide.subHeading"
              />

            </div>
          </transition-group>
        </div>

        <div v-html="liquid.content" />

        <div
          v-if="liquid.mediaOptions === 'image' && slides.length > 1"
          class="full-image-carousel__progress"
        >
          <div
            ref="progressBar"
            class="full-image-carousel__progress--bar">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Full image carousel (full-image-carousel)
 * -----------------------------------------------------------------------------
 * Full image carousel.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.layout - layout, either `standard` or `fullscreen`.
 * @param {Boolean} liquid.overlayOptions - Add overlay, either `none`,
 * `fullscreen` or `third`.
 * @param {String} liquid.mediaOptions - Media options, either `image`, `video`.
 * @param {String} liquid.horizontalAlignmentOptions - Content horizontal
 * alignment options, either `left`, `center`, right`.
 * @param {String} liquid.verticalAlignmentOptions - Content vertical
 * alignment options, either `center`, `bottom`.
 * @param {String} liquid.mediaImage - HTML image markup.
 * @param {object} liquid.mobileVideo - Mobile video.
 * @param {object} liquid.desktopVideo - Desktop video.
 * @param {String} liquid.content - HTML content markup.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import VideoPlayer from '~async/video-player/video-player'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'FullImageCarousel',

  components: {
    VideoPlayer,
    Splide,
    SplideSlide,
    ResponsiveImage
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        layout: '',
        overlayOptions: '',
        mediaOptions: '',
        horizontalAlignmentOptions: '',
        verticalAlignmentOptions: '',
        mediaImage: '',
        responsiveImagePadding: {
          type: Boolean,
          default: false,
        },
        mobileVideo: {
          type: Object,
          default: () => ({}),
        },
        desktopVideo: {
          type: Object,
          default: () => ({}),
        },
        content: '',
        lazyload: false,
      }),
    },
    text: {
      type: Array,
      default: () => [],
    },
    slides: {
      type: Array,
      default: () => [],
    },
    slideInterval: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      currentSlideIndex: 0,
    };
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the best video src from the supplied video (based on breakpoint).
     */
    videoSource() {
      if (this.liquid.mediaOptions !== 'video') return null;
      return this.isMobile
        ? this.getMp4Source(this.liquid.mobileVideo)?.[0]
        : this.getMp4Source(this.liquid.desktopVideo)?.[0]
    },

    /**
     * Splide Options.
     */
    splideOptions() {
      return {
        type: 'fade',
        rewind: true,
        autoplay: true,
        interval: this.slideInterval * 1000,
        arrows: false,
        pagination: false,
        pauseOnHover: false,
        pauseOnFocus: true,
      };
    },
  },

  methods: {
    /**
     * Returns the highest resolution mp4 video source from the supplied video.
     * @param {Object} video - Shopify video object.
     * @returns {Array}
     */
    getMp4Source(video) {
      if (!video || !video.sources) return [];
      const sources = [...video.sources.filter(source => source.format === 'mp4')]
      return sources.sort((a, b) => b.width - a.width)
    },

    /**
     * On Splide mount set up.
     * @param {Object} splide - Splide
    */
    onSplideMounted(splide) {
      const end = splide.Components.Controller.getEnd() + 1
      const rate = Math.min((splide.index + 1) / end, 1)

      this.$refs.progressBar.style.width = `${String(100 * rate)}%`
    },

    /**
     * On Splide move.
     * @param {Object} splide - Splide
    */
    onSplideMoved(splide, newIndex) {
      this.currentSlideIndex = newIndex;

      const end = splide.Components.Controller.getEnd() + 1
      const rate = Math.min((splide.index + 1) / end, 1)

      this.$refs.progressBar.style.width = `${String(100 * rate)}%`
    },
  },
}
</script>

<style lang="scss">
@import './full-image-carousel';
</style>
